import JsZip from 'jszip';
import { useEffect, useState } from 'react';
import useNotification from '../../hooks/useNotification';
import { downloadBlob } from '../../utils/downloadBlob';
import { downloadFile } from '../../utils/downloadFile';
import Button from './Button';

export function DownloadZipButton({
  assetUrls,
  zips,
  fileName,
  text,
  type,
  onClick,
  fileNames,
}: {
  assetUrls: string[];
  zips: string[];
  fileName: string;
  text?: string;
  type?: 'primary' | 'secondary' | undefined;
  onClick?: () => void;
  fileNames?: string[];
}) {
  const hasThingsToZip = assetUrls?.length > 0 || zips?.length > 0;
  const [loading, setLoading] = useState(false);

  const { showNotification } = useNotification();
  const handleZipDownload = async () => {
    if (onClick) onClick();

    setLoading(true);
    // Download available zips instantly
    // Need to delay to allow for multiple zips to trigger at once
    if (zips?.length > 0) {
      showNotification(
        {
          message: `Creating zip${zips.length > 1 ? 's' : ''} for ${zips.length} video pack${
            zips.length > 1 ? 's' : ''
          }.`,
        },
        zips.length * 2000
      );
    }
    zips?.forEach((zip, i) => {
      setTimeout(() => {
        downloadFile(zip);
      }, 2000 * i);
    });

    // Stagger the images to load after the video zips start downloading
    setTimeout(async () => {
      // Build aand download zip from the assetUrls
      if (assetUrls.length > 0) {
        if (assetUrls.length > 1)
          showNotification({
            message: `Creating zip for ${assetUrls.length} image${
              assetUrls.length > 1 ? 's' : ''
            }.`,
          });

        const images = await Promise.all(
          assetUrls?.map((url) => fetch(url).then((resp) => resp.blob()))
        );

        const zip = JsZip();
        images.forEach((blob, i) => {
          const fileNameBasedOnUrl = assetUrls[i]
            .split('/')
            .pop()
            ?.split('#')[0]
            .split('?')[0] as string;
          const fileNamePassedIn = fileNames ? fileNames[i] : undefined;
          const fileName = fileNamePassedIn || fileNameBasedOnUrl;
          zip.file(fileName, blob);
        });

        const blob = await zip.generateAsync({ type: 'blob' });
        downloadBlob(blob, fileName);
      }
      setLoading(false);
    }, zips.length * 2000);
  };

  // show notification if loading takes longer
  useEffect(() => {
    if (loading) {
      const timeout = setTimeout(() => {
        showNotification({
          message: 'Still preparing your zip.',
        });
      }, 6000);
      const timeoutSlow = setTimeout(() => {
        showNotification(
          {
            message: 'Leave this tab open and the zip will download when ready.',
          },
          10000
        );
      }, 20000);
      return () => {
        clearTimeout(timeout);
        clearTimeout(timeoutSlow);
      };
    }
  }, [loading, showNotification]);

  return (
    <Button onClick={handleZipDownload} loading={loading} type={type} disabled={!hasThingsToZip}>
      {text || 'Download Zip'}
    </Button>
  );
}
